import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule, scheduleOnce } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

import type { APIData, PopupData } from 'webapp-types';

import didInsert from 'webapp/modifiers/did-insert';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormSelectComponent from 'webapp/components/form/select';
import FormTextareaComponent from 'webapp/components/form/textarea';
import PopupInnerModalComponent, { type Args } from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class CustomQuerySetupPopupTransformAttributeComponent extends PopupInnerModalComponent<PopupData.CustomQuery.TransformAttributes> {
	@tracked rows: { from: string; to: string | null; label: string }[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const initialData =
			this.form.transformsData?.[this.form.selectedPreviewDimension]?.[this.dataProperty] ||
			(this.isAttributeMap ? {} : []);

		if (this.isAttributeMap) {
			// map attributes
			const data =
				initialData as APIData.CustomQuery.TransformationsDimensionDataAttributesMap;
			Object.keys(initialData).forEach((key) => {
				this.rows.push({ from: key, to: data[key] || null, label: key });
			});
		} else {
			// skip attributes
			const data =
				initialData as APIData.CustomQuery.TransformationsDimensionDataAttributesSkip;
			data.forEach((item) => {
				this.rows.push({ from: item, to: null, label: this.getLabel(item) });
			});
		}
	}

	get title() {
		const attributeName = this.popupData.form.selectedPreviewDimensionName;
		return this.isAttributeMap
			? `Rename dimension values (${attributeName})`
			: `Exclude dimension values (${attributeName})`;
	}

	get form() {
		return this.popupData.form;
	}

	get dataProperty() {
		return this.popupData.dataProperty;
	}

	get isLoading() {
		return this.form.transformsData === null;
	}

	get isAttributeMap() {
		return this.dataProperty === 'attributesMap';
	}

	get possibleAttributes() {
		return this.popupData.possibleAttributes;
	}

	get unusedAttributes() {
		const usedAttributes = this.rows.map((r) => r.from),
			unusedAttributes: LabelValuePairString[] = [];
		this.possibleAttributes.forEach((item) => {
			if (!usedAttributes.includes(item.originalName)) {
				unusedAttributes.push({
					label: item.current,
					value: item.originalName,
				});
			}
		});
		return unusedAttributes;
	}

	private getLabel(originalName: string): string {
		const transformed =
			this.form.transformsData?.[this.form.selectedPreviewDimension]?.attributesMap?.[
				originalName
			];

		if (!transformed) {
			return originalName;
		}

		return `${transformed} (${originalName})`;
	}

	// ---- add/remove rows ----

	@tracked newKey = '';
	@tracked newValue = '';

	rowAddNode: HTMLElement | null = null;

	get isNewRowValid() {
		return this.newKey && (!this.isAttributeMap || this.newValue);
	}

	@action
	changeKey(value: string) {
		this.newKey = value;
	}

	@action
	addCustomKey() {
		return true;
	}

	@action
	addRow() {
		if (this.isNewRowValid) {
			this.rows = [
				...this.rows,
				{
					from: this.newKey,
					to: this.newValue || null,
					label: this.getLabel(this.newKey),
				},
			];
			this.newKey = '';
			this.newValue = '';
			this.scheduleReposition();
			this.scheduleScrollDownToNew();
		}
	}

	@action
	removeRow(value: string) {
		this.rows = this.rows.filter((r) => r.from !== value);
		this.scheduleReposition();
	}

	// ---- controls ----

	@action
	saveTransforms(): void {
		const values: SomeDataObject = this.isAttributeMap ? {} : [];

		if (!this.submitInProgress) {
			this.addRow();
			this.submitInProgress = true;

			this.rows.forEach((row) => {
				if (this.isAttributeMap) {
					values[String(row.from)] = row.to!;
				} else {
					(values as APIData.CustomQuery.TransformationsDimensionDataAttributesSkip).push(
						row.from,
					);
				}
			});
			this.form.updateTransforms(this.dataProperty, values);
			this.submitInProgress = false;
			this.close();
		}
	}

	@action
	initializeRowAdd(rowAddNode: HTMLElement) {
		// first insert
		this.rowAddNode = rowAddNode;
		this.scheduleReposition();
		this.scheduleScrollDownToNew();

		rowAddNode.addEventListener('keydown', this.handleRowAdd);
		rowAddNode.addEventListener('input', (e) => this.handleRowAdd(e as InputEvent));
	}

	scheduleScrollDownToNew() {
		if (!this.isLoading) {
			scheduleOnce('afterRender', this, this.scrollDownToNew);
		}
	}

	@action
	scrollDownToNew() {
		if (this.rowAddNode) {
			const newRow = this.rowAddNode.querySelector('.row.add');
			if (newRow) {
				newRow.scrollIntoView();
			}
		}
	}

	@action
	private handleRowAdd(e: KeyboardEvent | InputEvent) {
		if ((e as KeyboardEvent).key === 'Tab' && this.isNewRowValid) {
			this.addRow();
			schedule('afterRender', () => {
				const row = this.rowAddNode;
				if (row?.classList.contains('double')) {
					const select = row.querySelector('select');
					if (select) {
						$(select).select2('open');
					}
				}
				row?.querySelector('input')?.focus();
			});
		}
	}
	<template>
		<div class="title">
			{{this.title}}
		</div>

		<div class="content">
			<div id="transformsList">
				{{#if this.isLoading}}
					<BarLoaderComponent @size="big" />
				{{else}}
					{{#each this.rows as |row|}}
						<div class="row">
							<div class={{if this.isAttributeMap "double" "single"}}>
								<FormTextareaComponent @value={{row.label}} disabled={{true}} />
							</div>
							{{#if this.isAttributeMap}}
								<div class="double connected">
									<FormTextareaComponent @value={{row.to}} disabled={{true}} />
								</div>
							{{/if}}
							<a
								role="button"
								class="rem"
								{{on "click" (fn this.removeRow row.from)}}
							>
								<CommonUiIconComponent @name="Trash" @size="sm" />
							</a>
						</div>
					{{/each}}
					<div class="row add" {{didInsert this.initializeRowAdd}}>
						<div class={{if this.isAttributeMap "double" "single"}}>
							<FormSelectComponent
								name="newKey"
								@selected={{this.newKey}}
								@options={{this.unusedAttributes}}
								@onChange={{this.changeKey}}
								@isNumeric={{false}}
								@placeholder="Select dimension value"
								@onCustomAdd={{this.addCustomKey}}
								@isDisabled={{this.submitInProgress}}
								@isRequired={{true}}
							/>
						</div>
						{{#if this.isAttributeMap}}
							<div class="double connected">
								<FormTextareaComponent
									name="value"
									@value={{this.newValue}}
									placeholder="Enter new name"
									maxlength="1000"
									disabled={{this.submitInProgress}}
								/>
							</div>
						{{/if}}
						{{#if this.isNewRowValid}}
							<a role="button" class="add" {{on "click" this.addRow}}></a>
						{{/if}}
					</div>
				{{/if}}
			</div>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Save"
						@extraClass="save"
						@onTrigger={{this.saveTransforms}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>
		</div>
	</template>
}
